<template>
  <section class="order-item">
    <router-link
      class="order-item-link no-underline"
      :to="order.partner.code !== 'insurance' ? localizedRoute(`/my-account/orders/${order.id}`) : ''"
      :style="{'cursor': partner.code !== 'insurance' ? 'pointer' : 'default'}"
    >

      <!-- Order photo -->
      <div class="order-item-link-photo">
        <img
          :src="s3Path(order.partner.logo_path)"
          alt="partner_logo"
          loading="lazy"
          fetchpriority="auto"
          decoding="sync"
          width="73"
          height="45"
        >
      </div>
      <!---->

      <!-- Order content -->
      <div class="order-item-link-content">

        <!-- Details about order -->
        <div class="order-item-link-content-details">
          <div class="order-item-link-content-details-naming">
            <h3 class="order-item-link-content-details-naming-title" v-if="order.partner">
              {{ order.partner.name }}
              <span
                v-if="!order.is_paid && checkOnlinePayment(order.payment_method_code)"
                class="material-icons error-icon"
              >
                error_outline
              </span>
            </h3>
            <div class="order-item-link-content-details-naming-data">
              <p class="order-item-link-content-details-naming-data-order-id">
                <span class="order-item-link-content-details-naming-data-text data-id"># {{ order.connection_order_id }} </span>
                <span class="order-item-link-content-details-naming-data-text data-date"> {{ getMoment(order.created_at) }} </span>
                <span v-if="customerStatusLabel" class="order-item-link-content-details-naming-data-text data-status"> {{ customerStatusLabel }} </span>
              </p>
              <div class="refund-msg" v-if="getFullRefund && order.partner_code !== 'insurance' && !isAnywhere">
                <span class="refund-msg-text"> {{ $t('Funds returned') }} </span>
              </div>
            </div>
          </div>

          <!-- Order items -->
          <div class="order-item-link-content-details-list" v-if="!isAnywhere">
            <div class="order-item-link-content-details-list-empty" v-if="!order.items.length">
              {{ $t('No products listed for this order') }}
            </div>
            <div class="order-item-link-content-details-list-item"
                 v-for="(item, itemNumber) in order.items"
                 :key="item.id"
                 v-if="itemNumber < 6"
            >
              <div v-if="order.partner_code !== 'insurance'">
                <span class="order-item-link-content-details-list-item-qty"> {{ formatQty(item) + 'x' }} </span>
                <span class="order-item-link-content-details-list-item-title"> {{ item.name }} </span>
              </div>
              <div class="order-item-insurance" v-else>
                <div class="order-item-insurance-name">{{item.name}}</div>
                <div class="order-item-insurance-description" v-if="item && item.description" v-html="item.description"/>
              </div>
            </div>
            <router-link v-if="order.items.length > 6" class="order-item-link-all-item no-underline" :to="localizedRoute(`/my-account/orders/${order.id}`)">
              {{ $t('View all products') }}
            </router-link>
          </div>
          <div class="order-item-link-content-details-list" v-else>
            <h5 class="my5">{{ $t('Comment to the order') }}</h5>
            <div v-if="order.comment">
              <p class="my5 anywhere-comment">{{ order.comment }}</p>
            </div>
            <router-link class="order-item-link-all-item no-underline" :to="localizedRoute(`/my-account/orders/${order.id}`)">
              {{ $t('View details') }}
            </router-link>
          </div>
          <!---->

        </div>
        <!---->

        <!-- Price and action -->
        <div class="order-item-link-content-line">
          <span class="order-item-link-content-line-price">
            {{ !getFullRefund ? amountToPayWithFee : order.original_subtotal | price }}
            <span v-if="checkOnlinePayment(order.payment_method_code)">({{ $t('including commission') }} 2,3%)</span>
          </span>

          <div class="order-item-link-content-line-actions" v-if="order.partner_code !== 'insurance' && !isAnywhere">
            <button class="order-item-link-content-line-actions-btn"
                    v-if="checkOnlinePayment(order.payment_method_code) && !order.is_paid"
                    @click.prevent="pay"
            >
              <span class="material-icons">credit_card</span>
              <span class="link"> {{ $t('Pay') }} </span>
            </button>
            <button class="order-item-link-content-line-actions-btn"
                    v-if="order.partner.enabled && order.partner.active"
                    @click.prevent="remakeOrder"
            >
              <span class="material-icons"> restart_alt </span>
              <span class="link"> {{ $t('Repeat') }} </span>
            </button>
          </div>
        </div>
        <!---->

      </div>
      <!---->

    </router-link>
    <reorder-list :order-id="order.id" :order="order" :products="products" :count="order.items.length" />
    <pay-order-modal v-if="!order.is_paid" :order="order" @calculateFee="calculateFee"/>
  </section>
</template>

<script async>
import moment from 'moment';
import { mapActions, mapGetters } from 'vuex';
import {s3Path} from "@vue-storefront/core/helpers";
import { Logger } from '@vue-storefront/core/lib/logger'
import { isOnlinePaymentMethod } from 'theme/helpers/paymentMethod'

const ReorderList = () => import(/* webpackChunkName: "vsf-reorder-list" */ 'theme/components/theme/blocks/Order/ReorderList.vue');
const PayOrderModal = () => import(/* webpackChunkName: "vsf-pay-order-modal" */ './Order/PayOrderModal');

export default {
  name: 'Order',
  data () {
    return {
      products: {},
      partner: {},
      isShowed: false,
      ipayFee: 0
    }
  },
  props: {
    order: {
      type: Object,
      required: true
    }
  },
  components: {
    ReorderList,
    PayOrderModal
  },
  computed: {
    ...mapGetters({
      getPartnerByCode: 'partners/getPartnerByLocationAndByPartnerCode',
      locationActive: 'locations/getActiveLocation'
    }),
    getOrderGrandTotal () {
      return this.order.subtotal + this.order.shipping_amount
    },
    getFullRefund () {
      return this.order.refunded === this.order.original_grand_total
    },
    isAnywhere () {
      return (!this.order.partner || this.order.partner.code === 'anywhere') && this.order.partner.code === 'anywhere'
    },
    // Customer-facing status (received/preparing/on_the_way/delivered/on_hold/
    // cancelled — a curated set derived from the internal lifecycle state,
    // never the raw internal status). Falls back to the legacy status string
    // for an older/uncached API response that predates this field.
    customerStatusLabel () {
      const labels = {
        received: this.$t('Received'),
        preparing: this.$t('Preparing'),
        on_the_way: this.$t('On the way'),
        delivered: this.$t('Delivered'),
        on_hold: this.$t('On hold'),
        cancelled: this.$t('Cancelled')
      }
      if (this.order.customer_status && labels[this.order.customer_status]) {
        return labels[this.order.customer_status]
      }
      if (!this.order.status) return ''
      return this.order.status.charAt(0).toUpperCase() + this.order.status.slice(1)
    },
    amountToPay () {
      return parseFloat(this.order.grand_total).toFixed(4)
    },
    amountToPayWithFee () {
      return parseFloat(Number(this.amountToPay) + Number(this.ipayFee)).toFixed(4)
    }
  },
  methods: {
    s3Path,
    ...mapActions({
      getItems: 'user/getOrderItems',
      changeCurrentPartner: 'partners/changeCurrentPartner',
      loadPartners: 'partners/loadPartners',
      changeLocation: 'locations/setCurrentLocation',
      OnAndOffLoader: 'ui/OnAndOffLoader'
    }),
    async pay () {
      this.$bus.$emit('modal-show', `pay-order-${this.order.id}`)
    },
    async remakeOrder () {
      this.$store.commit('ui/setLoader', true)
      this.$store.dispatch(
        'user/getOrderItemsForReorder',
        this.order.id
      ).then(async resp => {
        this.count = resp.available.length + resp.unavailable.length;
        this.$bus.$emit('notification-progress-stop')
        this.products = { available: resp.available, notAvailable: resp.unavailable, count: this.count }
        this.$bus.$emit('modal-toggle', 'reorder-list' + this.order.id)
      }).catch(error => {
        Logger.error('Cannot load order items for reordering ', error)()
      }).finally(() => {
        this.$store.commit('ui/setLoader', false)
      })
    },
    getMoment (date) {
      return moment(date).format('DD.MM.YYYY')
    },
    formatQty (item) {
      return item.is_qty_decimal == 1 ? parseFloat(item.qty.toFixed(2)) : item.qty
    },
    checkOnlinePayment (payment) {
      return isOnlinePaymentMethod(payment)
    },
    calculateFee (value) {
      this.ipayFee = value
    }
  }
}
</script>

<style lang="scss" scoped>
  .order-link:not(.no-underline):hover:after {
    position: absolute;
    background: none;
  }

  .refund-msg {
    &-text {
      color: #11bb2e;
      font-size: 14px;
      font-weight: 600;
      line-height: 1;

      display: inline-flex;
    }
  }

  .order-item {
    padding: 10px;

    position: relative;

    border-radius: 10px;
    transition: .2s;

    &:hover {
      cursor: pointer;
      background-color: #f5f5f5;

      &:before {
        display: none;
      }
    }

    &:before {
      width: 100%;
      height: 1px;

      position: absolute;
      bottom: 0;

      margin: auto;
      content: '';

      background-color: #ebebeb;
    }

    &-link {
      display: flex;

      &-photo {
        min-width: 73px;
        height: 73px;
        padding: 5px;

        display: flex;
        justify-content: center;
        align-items: center;

        border-radius: 10px;
        box-shadow: 1px 2px 7px 0 rgba(0, 0, 0, .15);
        background-color: #fff;

        & img {
          height: auto;
          max-width: 73px;
          max-height: 73px;

          margin: 0 auto;
        }
      }

      &-content {
        margin-left: 20px;
        flex: auto 1;

        &-details {
          min-height: 60px;

          &-naming {
            width: 100%;

            display: flex;
            justify-content: space-between;
            align-items: center;

            @media only screen and (max-width: 768px) {
              flex-direction: column;
              align-items: flex-start;
            }

            &-title {
              margin: 0;
              padding: 0;

              color: #111;
              font-size: 16px;
              font-weight: 600;
              display: flex;
              align-items: center;
              .error-icon {
                color: #ff0000;
                margin-left: 5px;
              }
            }

            &-data {
              padding-left: 10px;
              display: flex;
              flex-direction: column;
              align-items: flex-end;

              @media only screen and (max-width: 768px) {
                padding-left: 0;
                margin-top: 10px;
              }

              @media only screen and (max-width: 452px) {
                flex-direction: column;
              }

              &-order-id {
                margin: 0 0 4px 0;
              }

              &-text {
                color: #000;
                font-size: 14px;
                font-weight: 600;

                &:last-child {
                  margin-left: 8px;
                  font-weight: 400;

                  @media only screen and (max-width: 452px) {
                    margin-left: 0;
                  }
                }
              }
            }
          }

          &-list {
            margin-top: 10px;

            display: flex;
            flex-direction: column;
            align-items: flex-start;

            &-item {
              & + & {
                margin-top: 5px;
              }

              &-qty, &-title {
                color: #111;
                font-size: 14px;
              }

              &-qty {
                margin-right: 8px;
                font-weight: 600;
              }

              &-title {
                font-weight: 400;
              }
            }

            &-empty {
              color: #4a4a4a;
              font-size: 14px;
            }
          }
        }

        &-line {
          margin-top: 15px;

          display: flex;
          justify-content: space-between;
          align-items: center;

          @media only screen and (max-width: 991px) {
            flex-direction: column;
            align-items: flex-start;
          }

          @media only screen and (max-width: 576px) {
            flex-direction: row;
            align-items: center;
          }

          @media only screen and (max-width: 504px) {
            flex-direction: column;
            align-items: flex-start;
          }

          &-price {
            color: #111;
            font-size: 14px;
            font-weight: 600;

            @media only screen and (max-width: 768px) {
              margin-bottom: 8px;
            }

            @media only screen and (max-width: 576px) {
              margin-bottom: 0;
            }

            @media only screen and (max-width: 452px) {
              margin-bottom: 8px;
            }
          }

          &-actions {
            display: flex;

            @media only screen and (max-width: 991px) {
              width: 100%;
              margin-top: 10px;
              justify-content: space-between;
            }

            @media only screen and (max-width: 768px) {
              flex-direction: column;
            }

            @media only screen and (max-width: 576px) {
              width: auto;
              flex-direction: row;
            }

            @media only screen and (max-width: 504px) {
              width: 100%;
            }

            @media only screen and (max-width: 400px) {
              flex-direction: column;
            }

            &-btn {
              padding: 3px 8px;

              display: flex;
              align-items: center;

              background-color: #11bb2e;
              border: none;
              border-radius: 5px;

              color: #fff;
              font-size: 14px;
              font-weight: 400;

              outline: none;
              transition: .2s;

              @media only screen and (max-width: 991px) {
                width: 100%;
                justify-content: center;
              }

              @media only screen and (max-width: 576px) {
                width: auto;
              }

              @media only screen and (max-width: 504px) {
                width: 100%;
                justify-content: center;
              }

              &:hover {
                cursor: pointer;
                background-color: rgba(17, 187, 46, .7);
              }

              & + & {
                margin-left: 10px;

                @media only screen and (max-width: 768px) {
                  margin-top: 3px;
                  margin-left: 0;
                }

                @media only screen and (max-width: 576px) {
                  margin-top: 0;
                  margin-left: 10px;
                }

                @media only screen and (max-width: 400px) {
                  margin-top: 3px;
                  margin-left: 0;
                }
              }

              & .link {
                margin-left: 8px;
                color: #fff;
                text-decoration: none;
              }
            }
          }
        }
      }
      &-all-item {
        margin-top: 5px;
        color: #38bb2d;
      }
    }
  }
  .anywhere-comment {
    word-break: break-word;
  }
</style>
<style lang="scss">
.order-item-insurance-description {
  margin-top: 5px;
  font-size: 14px;
  p {
    margin: 0;
  }
}
</style>
